import { SupabaseClient } from "@supabase/supabase-js";
import { Database } from "../../supabase.types";
import { logException } from "@punchup/sentry";

const MAX_ROWS = 10_000;

/**
 * Returns the user IDs of all Punchup super-admins (internal staff).
 * Used to exclude internal accounts from outbound emails like the comedian
 * weekly digest and marketing outreach. Requires an admin-scoped client
 * because `super_admins` is RLS-protected.
 */
export async function fetchSuperAdminUserIds(
  supabase: SupabaseClient<Database>
): Promise<Set<string>> {
  const { data, error } = await supabase
    .from("super_admins")
    .select("user_id")
    .limit(MAX_ROWS);

  if (error) {
    logException(error, {
      data: "[super-admins] Failed to fetch super_admins",
    });
    throw error;
  }

  return new Set((data ?? []).map((row) => row.user_id));
}

/**
 * Whether `userId` is a super admin. Unlike `isSuperAdmin`, it checks a given
 * id, not the signed-in user. Requires an admin-scoped client; throws when the
 * lookup fails, rather than answering false.
 */
export async function isSuperAdminUserId(
  supabase: SupabaseClient<Database>,
  userId: string
): Promise<boolean> {
  const { data, error } = await supabase
    .from("super_admins")
    .select("user_id")
    .eq("user_id", userId)
    .maybeSingle();

  if (error) {
    logException(error, {
      data: `[super-admins] Failed to look up super admin ${userId}`,
    });
    throw error;
  }

  return data !== null;
}
